import { Fragment, useMemo, useState } from "react";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { FaqList } from "@/components/FaqList";
import { Eyebrow, Section, SectionHeading } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { addOns, faqs, plans, type Plan, type PlanId } from "@/data/pricing";
import { cn } from "@/lib/cn";

type BillingCycle = "monthly" | "annual";

const ANNUAL_DISCOUNT = 0.15;

const planStats: Record<PlanId, { brands: string; users: string }> = {
  starter: { brands: "1", users: "10" },
  professional: { brands: "3", users: "40" },
  enterprise: { brands: "Unlimited", users: "Unlimited" },
};

function billedPrice(plan: Plan, cycle: BillingCycle) {
  const numeric = Number(plan.price.replace(/[^0-9.]/g, ""));
  if (!numeric) {
    return {
      amount: plan.price,
      note: plan.priceNote,
      yearNote: null as string | null,
    };
  }
  if (cycle === "monthly") {
    return {
      amount: plan.price,
      note: plan.priceNote,
      yearNote: null as string | null,
    };
  }
  const monthlyEquivalent = Math.round(numeric * (1 - ANNUAL_DISCOUNT));
  const yearTotal = monthlyEquivalent * 12;
  return {
    amount: `$${monthlyEquivalent}`,
    note: "per month, billed annually",
    yearNote: `$${yearTotal.toLocaleString()} billed once a year`,
  };
}

type Cell = true | false | string;
type ComparisonRow = {
  label: string;
  detail?: string;
  values: Record<PlanId, Cell>;
};
type ComparisonCategory = { name: string; rows: ComparisonRow[] };

const comparison: ComparisonCategory[] = [
  {
    name: "Core",
    rows: [
      {
        label: "Brand / tenant identities",
        detail: "Parent brands, DBAs, and regional names under one tenant",
        values: { starter: "1", professional: "3", enterprise: "Unlimited" },
      },
      {
        label: "Office users included",
        values: { starter: "10", professional: "40", enterprise: "Unlimited" },
      },
      {
        label: "TrexField technician app",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "Customer self-service portal",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "Multi-location scheduling",
        values: { starter: false, professional: true, enterprise: true },
      },
      {
        label: "Role-based permissions",
        values: {
          starter: "Basic",
          professional: "Advanced",
          enterprise: "Advanced + custom roles",
        },
      },
    ],
  },
  {
    name: "Communications & AI",
    rows: [
      {
        label: "AI voice agents answer & book jobs",
        detail: "Metered separately — see usage bundles below",
        values: {
          starter: "Business hours",
          professional: "24/7",
          enterprise: "24/7 + custom scripts",
        },
      },
      {
        label: "AI-assisted dispatch",
        values: { starter: false, professional: true, enterprise: true },
      },
      {
        label: "Missed-call text-back",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "SMS & email sequences",
        values: {
          starter: "Manual templates",
          professional: "Automated sequences",
          enterprise: "Automated + custom triggers",
        },
      },
      {
        label: "Call recording & transcripts",
        values: { starter: false, professional: true, enterprise: true },
      },
      {
        label: "Live warm transfer to a CSR",
        values: { starter: false, professional: true, enterprise: true },
      },
    ],
  },
  {
    name: "Financials",
    rows: [
      {
        label: "Estimates & invoices",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "Online payments",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "Pricebook management",
        values: {
          starter: "Single pricebook",
          professional: "Multi-brand pricebooks",
          enterprise: "Multi-brand + approval workflows",
        },
      },
      {
        label: "Accounting & job costing",
        detail: "Available as an add-on — see modules below",
        values: {
          starter: "Add-on ($99/mo)",
          professional: "Add-on ($99/mo)",
          enterprise: "Included",
        },
      },
      {
        label: "Memberships & service agreements",
        values: { starter: true, professional: true, enterprise: true },
      },
      {
        label: "Revenue reporting",
        values: {
          starter: "Standard",
          professional: "Advanced",
          enterprise: "Advanced + custom dashboards",
        },
      },
    ],
  },
  {
    name: "Growth",
    rows: [
      {
        label: "Marketing automation & campaigns",
        values: { starter: false, professional: true, enterprise: true },
      },
      {
        label: "Review generation & reputation",
        values: { starter: false, professional: true, enterprise: true },
      },
      {
        label: "Inventory & purchase orders",
        values: {
          starter: "Add-on ($79/mo)",
          professional: "Add-on ($79/mo)",
          enterprise: "Included",
        },
      },
      {
        label: "Workforce scheduling extras",
        values: {
          starter: "Add-on ($49/mo)",
          professional: "Included",
          enterprise: "Included",
        },
      },
      {
        label: "Dedicated success manager",
        values: { starter: false, professional: false, enterprise: true },
      },
      {
        label: "Custom integrations & SSO",
        values: { starter: false, professional: false, enterprise: true },
      },
      {
        label: "API access",
        values: {
          starter: "Read-only",
          professional: "Full API",
          enterprise: "Full API + webhooks",
        },
      },
    ],
  },
];

function ComparisonCell({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <span
        aria-label="Included"
        className="text-lg font-semibold text-teal-deep"
      >
        ✓
      </span>
    );
  }
  if (value === false) {
    return (
      <span aria-label="Not included" className="text-muted">
        —
      </span>
    );
  }
  return <span className="text-sm font-medium text-fg">{value}</span>;
}

export function PricingPage() {
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const isAnnual = cycle === "annual";

  const priced = useMemo(
    () => plans.map((plan) => ({ plan, ...billedPrice(plan, cycle) })),
    [cycle],
  );

  return (
    <>
      <Head
        title="Pricing — ServiTrex plans for growing home-service teams"
        description="Starter, Professional, and Enterprise pricing for ServiTrex. Compare every capability, add accounting, inventory, and workforce modules, and see how AI voice usage is billed."
      />

      <section className="relative overflow-hidden pb-6 pt-14 sm:pt-16">
        <div className="container-page">
          <div className="grid items-end gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8">
            <div>
              <Eyebrow>Pricing</Eyebrow>
              <h1 className="max-w-3xl text-balance text-4xl text-fg sm:text-5xl lg:text-[3.4rem] lg:leading-[1.03]">
                Clear plans for one shop or a whole group of brands.
              </h1>
              <p className="mt-5 max-w-xl text-pretty text-lg text-muted sm:text-xl">
                Platform access is a flat monthly price. AI voice minutes, SMS,
                and AI credits scale with call volume, so you never pay for
                demand you haven&rsquo;t created yet.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <div
                  role="group"
                  aria-label="Billing period"
                  className="inline-flex rounded-full border border-border bg-surface-raised p-1 shadow-soft"
                >
                  <button
                    type="button"
                    aria-pressed={!isAnnual}
                    onClick={() => setCycle("monthly")}
                    className={cn(
                      "min-h-9 rounded-full px-4 text-sm font-semibold transition",
                      !isAnnual
                        ? "bg-ink text-fg-inverse"
                        : "text-muted hover:text-fg",
                    )}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    aria-pressed={isAnnual}
                    onClick={() => setCycle("annual")}
                    className={cn(
                      "min-h-9 rounded-full px-4 text-sm font-semibold transition",
                      isAnnual
                        ? "bg-ink text-fg-inverse"
                        : "text-muted hover:text-fg",
                    )}
                  >
                    Annual
                  </button>
                </div>
                <p className="text-sm font-medium text-teal-deep">
                  {isAnnual
                    ? `Locked in — you're saving ${Math.round(ANNUAL_DISCOUNT * 100)}% versus paying monthly.`
                    : `Save ${Math.round(ANNUAL_DISCOUNT * 100)}% by switching to annual billing.`}
                </p>
              </div>
            </div>

            <Reveal>
              <figure className="overflow-hidden rounded-[1.75rem] border border-border bg-surface-raised shadow-soft">
                <img
                  src="/img/mktg-hero-dashboard.jpg"
                  alt="ServiTrex dispatch dashboard showing live jobs, technician capacity, and brand context"
                  width={960}
                  height={720}
                  className="aspect-[4/3] w-full object-cover"
                />
                <figcaption className="px-5 py-4 text-sm text-muted">
                  Every plan runs on the same dispatch board — what changes is
                  how much of the platform is switched on.
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <Section className="pt-6">
        <div className="grid gap-5 lg:grid-cols-3">
          {priced.map(({ plan, amount, note, yearNote }, index) => (
            <Reveal key={plan.id} delay={index * 0.06}>
              <article
                className={cn(
                  "flex h-full flex-col rounded-[1.75rem] border border-border bg-surface-raised p-6 shadow-soft sm:p-7",
                  plan.featured &&
                    "border-teal/50 shadow-glow lg:-translate-y-2",
                )}
              >
                {plan.featured ? (
                  <p className="mb-3 w-fit rounded-full bg-teal/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-teal-deep">
                    Most popular
                  </p>
                ) : (
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted">
                    {plan.name}
                  </p>
                )}
                <h2 className="font-display text-3xl tracking-tight">
                  {plan.name}
                </h2>

                <div className="mt-4 flex items-end gap-2">
                  <span className="font-display text-4xl tracking-tight">
                    {amount}
                  </span>
                  <span className="pb-1 text-sm text-muted">{note}</span>
                </div>
                {yearNote ? (
                  <p className="mt-1 text-xs text-muted">{yearNote}</p>
                ) : null}

                <p className="mt-4 text-sm leading-relaxed text-muted">
                  {plan.description}
                </p>

                <dl className="mt-5 grid grid-cols-2 gap-3 rounded-2xl bg-surface-muted/70 p-3 text-center">
                  <div>
                    <dt className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted">
                      Brands
                    </dt>
                    <dd className="mt-1 font-display text-lg font-semibold tracking-tight">
                      {planStats[plan.id].brands}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted">
                      Users
                    </dt>
                    <dd className="mt-1 font-display text-lg font-semibold tracking-tight">
                      {planStats[plan.id].users}
                    </dd>
                  </div>
                </dl>

                <ul className="mt-6 flex-1 space-y-3 text-sm text-fg">
                  {plan.highlights.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span aria-hidden className="mt-1 text-teal-deep">
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  {plan.id === "enterprise" ? (
                    <Button
                      to="/demo"
                      className="w-full"
                      variant={plan.featured ? "primary" : "secondary"}
                    >
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      to={`/signup?plan=${plan.id}`}
                      className="w-full"
                      variant={plan.featured ? "primary" : "secondary"}
                    >
                      {plan.cta}
                    </Button>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="Feature comparison"
          title="Every capability, plan by plan"
          description="The bullet lists above are the highlights. This is the full picture — what's included, what's an add-on, and what only ships with Enterprise."
        />

        <div className="mt-10 overflow-x-auto rounded-[1.75rem] border border-border bg-surface-raised shadow-soft">
          <table className="w-full min-w-[680px] border-collapse text-left">
            <thead>
              <tr className="border-b border-border">
                <th
                  scope="col"
                  className="sticky left-0 z-10 min-w-[14rem] bg-surface-raised px-5 py-4 text-xs font-semibold uppercase tracking-[0.12em] text-muted sm:px-6"
                >
                  Capability
                </th>
                {plans.map((plan) => (
                  <th
                    key={plan.id}
                    scope="col"
                    className={cn(
                      "min-w-[9rem] px-4 py-4 text-sm sm:px-6",
                      plan.featured && "bg-teal/5",
                    )}
                  >
                    <span className="font-display text-base font-semibold tracking-tight text-fg">
                      {plan.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((category) => (
                <Fragment key={category.name}>
                  <tr className="border-b border-border bg-surface-muted/60">
                    <th
                      scope="colgroup"
                      colSpan={plans.length + 1}
                      className="sticky left-0 z-10 bg-surface-muted/60 px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep sm:px-6"
                    >
                      {category.name}
                    </th>
                  </tr>
                  {category.rows.map((row) => (
                    <tr
                      key={row.label}
                      className="border-b border-border last:border-0"
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 min-w-[14rem] bg-surface-raised px-5 py-4 text-sm font-medium text-fg sm:px-6"
                      >
                        {row.label}
                        {row.detail ? (
                          <span className="mt-1 block text-xs font-normal text-muted">
                            {row.detail}
                          </span>
                        ) : null}
                      </th>
                      {plans.map((plan) => (
                        <td
                          key={plan.id}
                          className={cn(
                            "px-4 py-4 text-sm sm:px-6",
                            plan.featured && "bg-teal/5",
                          )}
                        >
                          <ComparisonCell value={row.values[plan.id]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted sm:hidden">
          Scroll sideways to compare all three plans →
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Add-ons"
          title="Layer modules when the operation needs them"
          description="Keep the core plan lean, then turn on the systems that match your maturity. Enterprise includes every module from day one."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="grid gap-4 sm:grid-cols-2">
            {addOns.map((addOn, index) => (
              <Reveal key={addOn.name} delay={index * 0.05}>
                <article className="h-full rounded-3xl border border-border bg-surface-raised p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-display text-2xl tracking-tight">
                      {addOn.name}
                    </h3>
                    <p className="text-sm font-semibold text-teal-deep">
                      {addOn.price}
                    </p>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted">
                    {addOn.detail}
                  </p>
                  {"note" in addOn && addOn.note ? (
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted">
                      {addOn.note}
                    </p>
                  ) : null}
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <figure className="h-full overflow-hidden rounded-3xl border border-border bg-surface-raised shadow-soft">
              <img
                src="/img/mktg-ai-voice.jpg"
                alt="Visualization of an AI voice agent answering a customer call and booking a job"
                width={640}
                height={480}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <figcaption className="px-5 py-4 text-sm text-muted">
                AI voice, SMS, and AI credits are metered separately from
                platform seats.
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="mt-8 rounded-3xl border border-dashed border-border bg-surface-raised/80 p-5 text-sm leading-relaxed text-muted sm:p-6">
          <p className="font-semibold text-fg">Metered overages</p>
          <p className="mt-2">
            AI voice minutes, SMS, and AI credits are metered and included plans
            ship with a starting usage bundle sized to your call volume.
            Overages beyond the bundle bill at published per-unit rates — voice
            minutes, SMS segments, and AI credits each have their own published
            rate — so growth never forces a surprise platform swap or a
            mid-month upsell call.
          </p>
        </div>
      </Section>

      <Section narrow>
        <SectionHeading
          eyebrow="FAQ"
          title="Straight answers before you talk to sales"
        />
        <div className="mt-8">
          <FaqList items={faqs} />
        </div>
      </Section>

      <CtaBand
        title="Not sure which plan fits?"
        body="Tell us about brands, seats, and call volume. We'll map Starter, Professional, or Enterprise without the hard sell."
      />
    </>
  );
}
